import React, { useCallback, useEffect, useState } from 'react';
import { AUTH_INTERACTION_REQUIRED_EVENT, reconnect } from '../authFetch';

/**
 * Listens for the "interaction required" signal that authFetch dispatches when
 * a token can't be renewed silently, and shows a non-destructive reconnect
 * prompt. Re-authentication runs from the button click (a user gesture), so the
 * popup isn't blocked and the user's in-progress work on the page is preserved.
 *
 * Note: this is intentionally NOT a React error boundary — error boundaries only
 * catch errors thrown during render/lifecycle, not async errors from fetch calls.
 */
export default function ReconnectPrompt() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onNeeded = () => setVisible(true);
    window.addEventListener(AUTH_INTERACTION_REQUIRED_EVENT, onNeeded);
    return () => window.removeEventListener(AUTH_INTERACTION_REQUIRED_EVENT, onNeeded);
  }, []);

  const handleReconnect = useCallback(async () => {
    setBusy(true);
    try {
      await reconnect();   // popup; on success the cached token is renewed
      setVisible(false);   // hide so the user can retry their action
    } catch {
      // redirect fallback navigates away; nothing more to do here
    } finally {
      setBusy(false);
    }
  }, []);

  if (!visible) return null;

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-labelledby="reconnect-title">
      <div style={dialogStyle}>
        <h3 id="reconnect-title" style={{ margin: '0 0 8px' }}>Session expired</h3>
        <p style={{ margin: '0 0 16px' }}>Your session needs to be renewed to continue.</p>
        <button onClick={handleReconnect} disabled={busy} style={buttonStyle}>
          {busy ? 'Reconnecting…' : 'Reconnect'}
        </button>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};

const dialogStyle = {
  backgroundColor: '#fff',
  color: '#111',
  padding: '24px 28px',
  borderRadius: 8,
  maxWidth: 360,
  textAlign: 'center',
  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
};

const buttonStyle = {
  padding: '10px 20px',
  fontSize: 16,
  cursor: 'pointer',
};
